import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import Modal from 'components/Modal';
import styled from 'styled-components';
import { Form } from 'antd';
import Button from 'components/Button';
import ChangeServiceModal, {
  ChangeServiceModalRef,
} from 'features/ticketList/pages/TicketDetailsPage/components/ChangeServiceModal';
import { IServiceItemData } from 'services/shop/types/categories';
import Add from 'assets/Icons/add';
import useGetListServiceCode from 'features/turn/services/hooks/useGetListServiceCode';
import turnApis from 'features/turn/services/apis';
import { getMessageError, IResponseDataBody } from 'services/response';
import toast from 'utils/toast';
import ConfigCard from './ConfigCard';
import colorTheme from 'constants/color';
import uuid from 'utils/uuid';

export type OpenConfigServiceCodeProps = {};

type ConfigServiceCodeRef = {
  open: () => void;
};

export const useConfigServiceCodeRef = () => useRef<ConfigServiceCodeRef>(null);

interface ConfigServiceCode {
  id?: string;
  code: string;
  services: {
    id: string;
    serviceName: string;
  }[];
}

interface ConfigServiceCodeFormValues {
  config: ConfigServiceCode[];
}

const ConfigServiceCode = forwardRef<ConfigServiceCodeRef>((_, ref) => {
  const [visible, setVisible] = useState(false);
  const [form] = Form.useForm<ConfigServiceCodeFormValues>();
  const useChangeServiceModalRef = useRef<ChangeServiceModalRef>(null);
  const { listServiceCode, mutate } = useGetListServiceCode(true);

  useImperativeHandle(ref, () => ({
    open() {
      setVisible(true);
      const initialValues: ConfigServiceCodeFormValues = {
        config: listServiceCode
          ?.filter((item) => item.code !== null)
          .map((item) => ({
            id: item.id,
            code: item.code,
            services: item.services.map((service) => ({
              id: service.id,
              serviceName: service.serviceName,
            })),
          })) || [
          {
            code: '',
            services: [],
          },
        ],
      };
      form.setFieldsValue(initialValues);
    },
  }));

  const handleClose = () => {
    setVisible(false);
    form.resetFields();
  };

  const handleSelectedServices = (rowIndex: number, services: IServiceItemData[]) => {
    const mappingServices = services.map((service) => ({
      id: service.id,
      serviceName: service.serviceName,
    }));

    form.setFieldValue(['config', rowIndex, 'services'], mappingServices);
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      const body = values.config.map((item) => ({
        id: item.id ? item.id : uuid(),
        code: item.code,
        serviceIds: item.services.map((service) => service.id),
      }));
      const res: IResponseDataBody = await turnApis.updateServiceCode(body);

      if (res?.data?.data) {
        mutate();
        toast.success('Update service code successfully');
        handleClose();
      }
    } catch (error) {
      toast.error(getMessageError(error) || 'Update service code failed');
    }
  };

  if (!visible) return null;
  return (
    <Modal
      modalTitle='CONFIG SERVICE GROUP'
      visible={visible}
      onClose={handleClose}
      onSubmit={() => form.submit()}
      width={'1600px'}
      noneBodyStyle={true}
      noneFooterStyle={true}
      footerPadding={0.5}
      v2
    >
      <Container>
        <Form form={form} layout='vertical' onFinish={onSubmit}>
          <Form.List name='config'>
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => (
                  <ConfigCard
                    key={field.name}
                    field={field}
                    form={form}
                    remove={remove}
                    onSelectService={() => {
                      const services = form.getFieldValue(['config', field.name, 'services']) || [];

                      useChangeServiceModalRef.current?.open(
                        (_, __, ___, selectedServices) => {
                          handleSelectedServices(field.name, selectedServices || []);
                        },
                        undefined,
                        undefined,
                        services.map((x: any) => x.id),
                      );
                    }}
                    defaultOpen={index === 0}
                  />
                ))}

                <Button
                  onClick={() => add({ 
                    code: `${'CODE_' + (fields.length + 1)}`,
                    services: [] 
                  })}
                  iconElement={<Add fill={colorTheme.teal_5} />}
                  className='add-config-btn'
                >
                  Add Config
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Container>
      <ChangeServiceModal ref={useChangeServiceModalRef} isMultiple />
    </Modal>
  );
});

ConfigServiceCode.displayName = 'ConfigServiceCode';
export default ConfigServiceCode;

const Container = styled.div`
  padding: 24px;
  font-family: Poppins;

  .ant-form-item {
    margin-bottom: 0;
  }

  .CONTENT_2 {
    font-family: Poppins;
  }

  .add-config-btn {
    border-radius: 999px;
    padding: 0 24px;
    border: 1px dashed ${colorTheme.teal_5}!important;
    background: ${colorTheme.v2_neutral_1};

    .color {
      color: ${colorTheme.teal_5};
      font-family: Poppins;
      font-size: 18px;
    }
  }
`;
